import React, { FC, MouseEvent, useEffect, useState } from 'react'

import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
  MouseSensor,
} from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable'

import {
  CreateNewCommonContentFile,
  CommonContentUploadedAsset,
  CommonContentsAssetSortItem,
} from 'src/components/organisms'

import {
  useCommonContentAsset,
  useCommonContentPageLoading,
  useGetCommonContentPages,
  useToast,
  useAuthority,
} from 'src/hooks'
import { CommonContentAssetEntity } from 'src/types'

type Props = {
  commonContentPageId: number
  assets: CommonContentAssetEntity[]
  className?: string
}

export const CommonContentUploadFileList: FC<Props> = ({
  commonContentPageId,
  assets,
  className,
}) => {
  const [dndAssets, setDndAssets] = useState(assets)

  const { reorderCommonContentAssets } = useCommonContentAsset()
  const { refetch } = useGetCommonContentPages()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { showCommonContentPageLoading, hideCommonContentPageLoading } =
    useCommonContentPageLoading()
  const { canWriteCommonContent, canDeleteCommonContent } = useAuthority()

  const shouldHandleEvent = (element: HTMLElement | null) => {
    let current = element

    while (current) {
      // data-no-dnd属性がついている要素はdnd-kitの対象外にする
      if (current.dataset && current.dataset.noDnd) {
        return false
      }
      current = current.parentElement
    }

    return true
  }

  // 特定のdata属性がついている要素はdnd-kitの対象外にするためのSensor
  class CustomMouseSensor extends MouseSensor {
    static activators = [
      {
        eventName: 'onMouseDown' as const,
        handler: ({ nativeEvent: event }: MouseEvent): boolean => {
          return shouldHandleEvent(event.target as HTMLElement)
        },
      },
    ]
  }

  const sensors = useSensors(
    useSensor(CustomMouseSensor, {
      activationConstraint: {
        // 5pxドラッグした時にソート機能を有効にする。この対応をする事でドラッグ可能領域内のクリック操作を可能にする。
        distance: 5,
      },
    })
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over) {
      return
    }

    if (active.id !== over.id) {
      showCommonContentPageLoading()

      const oldIndex = dndAssets.map(item => item.id).indexOf(active.id as number)
      const newIndex = dndAssets.map(item => item.id).indexOf(over.id as number)

      const updatedAssets = arrayMove(dndAssets, oldIndex, newIndex)
      setDndAssets(updatedAssets)

      try {
        await reorderCommonContentAssets({
          commonContentPageId: assets[0].pageId,
          updatedCommonContentAssetIds: updatedAssets.map(asset => asset.id),
        })
        refetch()
        displayStandardTextToast('保存しました')
      } catch {
        displayErrorTextToast('並び替えに失敗しました')
      } finally {
        hideCommonContentPageLoading()
      }
    }
  }

  useEffect(() => {
    const sortedAssets = [...assets].sort((a, b) => a.priority - b.priority)
    setDndAssets(sortedAssets)
  }, [assets])

  return (
    <div className={className}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={dndAssets}
          strategy={rectSortingStrategy}
          disabled={!canWriteCommonContent}
        >
          <div className="tw-flex tw-gap-s2 tw-flex-wrap">
            {dndAssets.map((asset, index) => {
              let displayContentNumber = asset.priority

              // 表示するコンテンツより前に空のコンテンツがある場合、表示するコンテンツの番号を調整する
              if (index !== 0) {
                const beforeAssets = dndAssets.slice(0, index)
                const emptyAssetCount = beforeAssets.filter(
                  beforeAsset => beforeAsset.contentType === 'empty'
                ).length
                displayContentNumber = asset.priority - emptyAssetCount
              }

              return (
                <CommonContentsAssetSortItem id={asset.id} key={asset.id}>
                  <CommonContentUploadedAsset
                    asset={asset}
                    displayContentNumber={displayContentNumber}
                    key={asset.id}
                    canEdit={canWriteCommonContent}
                    canDelete={canDeleteCommonContent}
                  />
                </CommonContentsAssetSortItem>
              )
            })}

            {canWriteCommonContent && (
              <CreateNewCommonContentFile
                commonContentPageId={commonContentPageId}
                commonContentAssetsCount={assets.length}
              />
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}
